import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import type { Story } from '../demo'

interface Props {
  stories: readonly Story[]
  onOpen: (index: number) => void
}

/**
 * The reels rail (reference.html:744–760).
 *
 * Every tile is a `ButtonBase`, not a tinted `<div>` with a click handler: the
 * rail is the only way into the story viewer, and a viewer nobody can open from
 * the keyboard is a viewer nobody can close from it either.
 *
 * The design draws a monogram over a flat tint rather than an image, which is
 * what a story looks like before anyone has uploaded artwork for it — there is
 * no stories endpoint (see `demo/fixtures.ts`), so that is also all we have.
 */
export const StoryReel = ({ stories, onOpen }: Props) => (
  <Stack
    direction="row"
    spacing={1.25}
    sx={{
      overflowX: 'auto',
      pb: 0.5,
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      '& > *': { flexShrink: 0 },
    }}
  >
    {stories.map((story, index) => (
      <ButtonBase
        key={story.key}
        onClick={() => onOpen(index)}
        aria-label={`${story.eyebrow}: ${story.label}`}
        sx={{
          width: 98,
          height: 152,
          borderRadius: '14px',
          bgcolor: story.tint,
          position: 'relative',
          overflow: 'hidden',
          display: 'block',
          textAlign: 'left',
          '&:active': { transform: 'scale(0.97)' },
        }}
      >
        <Typography
          aria-hidden
          sx={{
            position: 'absolute',
            top: 38,
            left: 0,
            right: 0,
            textAlign: 'center',
            fontWeight: 800,
            fontSize: 26,
            letterSpacing: '-0.5px',
            color: story.fg ?? YALO_SEMANTIC.fg1,
          }}
        >
          {story.initials}
        </Typography>

        {/* The play affordance and the scrim are decoration over a label that
            is already the button's name, so both are hidden from the tree. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(6,9,11,0) 45%, rgba(6,9,11,0.55) 100%)',
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 22,
            height: 22,
            borderRadius: `${YALO_RADIUS.pill}px`,
            bgcolor: 'rgba(255,255,255,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box component="svg" viewBox="0 0 24 24" width={10} height={10} fill={YALO_SEMANTIC.fg1}>
            <path d="M7 4.5v15l13-7.5Z" />
          </Box>
        </Box>

        <Typography
          sx={{
            position: 'absolute',
            left: 9,
            right: 9,
            bottom: 9,
            color: YALO_SEMANTIC.fgOnDark1,
            fontSize: 11.5,
            fontWeight: 700,
            lineHeight: '14px',
          }}
        >
          {story.label}
        </Typography>
      </ButtonBase>
    ))}
  </Stack>
)
